import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  TextField,
  MenuItem,
  Tabs,
  Tab,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  TablePagination,
  Popover
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import DeleteIcon from '@mui/icons-material/Delete';
import MenuList from '../MenuList';
import InfoIcon from '@mui/icons-material/Info'; // Import InfoIcon
import { orange } from '@mui/material/colors';
import AddSection from './AddSection';

const AppetiteDocManagement = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedLob, setSelectedLob] = useState('');
  const [lobs, setLobs] = useState([]);
  const [selectedSubLob, setSelectedSubLob] = useState('');
  const [selectedProfile, setSelectedProfile] = useState('');
  const [subLobs, setSubLobs] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [softRules, setSoftRules] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [tabSections, setTabSections] = useState([]);
  const [isSelectedProfileActive, setIsSelectedProfileActive] = useState(false);
  const [selectedSectionId, setSelectedSectionId] = useState('');
  const [selectedRuleIndex, setSelectedRuleIndex] = useState(null);
  const [ruleProperties, setRuleProperties] = useState([]);
  const [customerId, setCustomerId] = useState(''); // Customer ID state
  const [setShowErrors] = useState(false);
  const [successMessage, setSuccessMessage] = useState(''); // Success message state
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  // Fetch LOB List
  const fetchLobList = async () => {
    try {
      const response = await axios.get(
        `https://insureai-appetite-mgmt-1074099157700.us-central1.run.app/lobs/`
      );
      setLobs(response.data);
    } catch (error) {
      console.error('Error fetching application data:', error);
    }
  };

  const fetchSubLobs = async lobId => {
    try {
      const response = await axios.get(
        `https://insureai-appetite-mgmt-1074099157700.us-central1.run.app/sub-lobs/by-lob/${lobId}`
      );
      setSubLobs(response.data);
    } catch (error) {
      console.error('Error fetching application data:', error);
    }
  };

  function filterAppetiteProfiles (data, lob_id, sub_lob_id) {
    const filteredProfiles = data.filter(
      profile => profile.lob_id === lob_id && profile.sub_lob_id === sub_lob_id
    );
    return filteredProfiles;
  }

  const fetchProfiles = async subLobId => {
    try {
      const response = await axios.get(
        `https://insureai-appetite-mgmt-1074099157700.us-central1.run.app/appetite-profiles/`
      );
      const filteredProfiles = filterAppetiteProfiles(
        response.data,
        selectedLob,
        subLobId
      );
      setProfiles(filteredProfiles);
    } catch (error) {
      console.error('Error fetching application data:', error);
    }
  };

  const fetchRules = async () => {
    if (!selectedSectionId) return;

    try {
      const response = await axios.get(
        `https://insureai-appetite-mgmt-1074099157700.us-central1.run.app/flags/by-section/${selectedSectionId}?skip=0&limit=1000`
      );
      setSoftRules(response.data);
    } catch (error) {
      setSoftRules([]);
    }
  };

  const fetchProperties = async ruleId => {
    try {
      const response = await axios.get(
        `https://insureai-appetite-mgmt-1074099157700.us-central1.run.app/flag-properties/flag/${ruleId}`
      );
      setRuleProperties(response.data);
    } catch (error) {
      setRuleProperties([]);
    }
  };

  useEffect(() => {
    fetchLobList();
  }, []);

  useEffect(() => {
    if (lobs.length > 0) {
      setSelectedLob(lobs[0].id);
      fetchSubLobs(lobs[0].id);
    }
  }, [lobs]);

  useEffect(() => {
    if (subLobs.length > 0) {
      setSelectedSubLob(subLobs[0].id);
      fetchProfiles(subLobs[0].id);
    }
  }, [subLobs, selectedLob]);

  useEffect(() => {
    if (profiles.length > 0) {
      const firstProfile = profiles[0];
      setSelectedProfile(firstProfile.id);
      setCustomerId(firstProfile.customer_id);
      setIsSelectedProfileActive(firstProfile.status === 'active');
      setTabSections(firstProfile.sections || []);

      // Set the selected section ID to the first section's ID
      if (firstProfile.sections && firstProfile.sections.length > 0) {
        setSelectedSectionId(firstProfile.sections[0].id);
      } else {
        setSelectedSectionId(''); // Or some default value if no sections exist
      }
    }
  }, [profiles, selectedSubLob]);

  useEffect(() => {
    fetchRules();
  }, [selectedSectionId]);

  // Handle LOB Dropdown change
  const handleLobChange = event => {
    const selectedValue = event.target.value;
    setSelectedLob(selectedValue);
    fetchSubLobs(event.target.value);
  };

  // Handle dependent dropdown change
  const handleSubLobChange = event => {
    setSelectedSubLob(event.target.value);
    fetchProfiles(event.target.value);
  };

  const handleProfileChange = event => {
    const profileId = event.target.value;
    setSelectedProfile(profileId);

    const selectedProfile = profiles.find(profile => profile.id === profileId);
    setCustomerId(selectedProfile.customer_id);
    setTabSections(selectedProfile?.sections || []);
    setIsSelectedProfileActive(selectedProfile?.status === 'active');

    // Update selectedSectionId when profile changes
    if (selectedProfile?.sections && selectedProfile?.sections.length > 0) {
      setSelectedSectionId(selectedProfile.sections[0].id);
    } else {
      setSelectedSectionId('');
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleTabChange = (event, newValue) => {
    setSelectedSectionId(newValue);
  };

  const toCamelCase = str => {
    if (!str) {
      return str; // Handle empty or null strings
    }

    str = str.toLowerCase(); // Convert to lowercase first

    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  useEffect(() => {
    fetchRules();
  }, [selectedSectionId]);

  const addSoftRule = () => {
    const newRules = [
      ...softRules,
      {
        id: softRules.length + 1,
        details: '',
        rule_type: '',
        isNew: true
      }
    ];
    setSoftRules(newRules);
    setPage(Math.ceil(newRules.length / rowsPerPage) - 1);
  };

  const removeSoftRule = index => {
    const updatedRules = softRules.filter((_, i) => i !== index);
    setSoftRules(updatedRules);
  };

  const handlePublish = async () => {
    try {
      await axios.post(
        `https://insureai-appetite-mgmt-1074099157700.us-central1.run.app/app_mgmt/publish_record?lob_code=${selectedLob}&sub_lob_code=${selectedSubLob}&customer_id=${customerId}&user_id=${selectedProfile}`
      );
      setSuccessMessage('Profile published successfully!');
      setShowErrors(false);
    } catch (error) {
      setErrorMessage('Error publishing profile.');
    }
  };

  const handleSave = async () => {
    // setShowErrors(true)
    // if (!description) {
    //   setErrorMessage('Description is required.')
    //   return
    // }

    const newRules = softRules.filter(rule => rule.isNew);

    if (newRules.length === 0) {
      setErrorMessage('No new rules to save.');
      return;
    }

    try {
      for (const rule of newRules) {
        const payload = {
          details: rule.details,
          rule_type: rule.rule_type,
          section_id: selectedSectionId,
          name: 'Appetite Management'
        };

        await axios.post(
          'https://insureai-appetite-mgmt-1074099157700.us-central1.run.app/flags/',
          payload
        );
      }

      setSuccessMessage('New rules saved successfully!');
      setShowErrors(false);
    } catch (error) {
      setErrorMessage('Error saving new rules.');
    }
  };

  const handleCancel = () => {
    //setSoftRules([])
  };

  // Handle pagination change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (event, index, ruleId) => {
    fetchProperties(ruleId);
    setSelectedRuleIndex(index);
    setAnchorEl(event.currentTarget);
    // setOpenDialog(true)
  };

  const getRowNumber = index => {
    return page * rowsPerPage + index + 1;
  };

  return (
    <div
      id='appetiteManagment'
      style={{ marginLeft: 'auto', marginRight: '0' }}
    >
      <div className='flex justify-between items-center mb-4'>
        <div className='flex items-center space-x-4'>
          <MenuList isOpen={isMenuOpen} toggleMenu={toggleMenu} />
          <h1 className='py-2 text-2xl text-orange-600'>Appetite Management</h1>
        </div>
      </div>
      <div className='flex mb-6 space-x-6 relative appetite-filter'>
        <div className='flex'>
          <TextField
            select
            label='Class Code'
            value={selectedLob}
            onChange={handleLobChange}
            className='bg-white w-64'
            variant='outlined'
          >
            {lobs.map((option, index) => (
              <MenuItem key={index} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
        </div>
        <div>
          {selectedLob && (
            <TextField
              select
              label='Line Of Business'
              value={selectedSubLob}
              onChange={handleSubLobChange}
              className='bg-white w-64'
              variant='outlined'
            >
              {subLobs.map((option, index) => (
                <MenuItem key={index} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        </div>
        <div>
          {selectedSubLob && profiles.length > 0 && (
            <TextField
              select
              label='Profile'
              value={selectedProfile}
              onChange={handleProfileChange}
              className='bg-white w-64'
              variant='outlined'
            >
              {profiles.map((option, index) => (
                <MenuItem key={index} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        </div>
        <div className='absolute right-5 top-4 mt-4'>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='40'
            height='40'
            viewBox='0 0 90 90'
            stroke='#999'
          >
            <g transform='translate(1.41 1.41) scale(1)' fill='#fff'>
              <path
                d='M 36.58 88.144 c -0.175 0 -0.35 -0.046 -0.506 -0.138 c -0.306 -0.18 -0.494 -0.508 -0.494 -0.862
            V 47.938 c 0 -2.855 -1.042 -5.607 -2.932 -7.748 L 0.25 3.519 c -0.26 -0.295 -0.324 -0.715 -0.162 -1.073 
            S 0.607 1.856 1 1.856 h 17.174 c 0.552 0 1 0.448 1 1 s -0.448 1 -1 1 H 3.218 l 30.929 35.01 
            c 2.214 2.506 3.433 5.728 3.433 9.072 v 37.503 l 14.841 -8.276 V 47.938 c 0 -3.344 1.219 -6.565 3.434 -9.072
            l 30.928 -35.01 H 33.499 c -0.552 0 -1 -0.448 -1 -1 s 0.448 -1 1 -1 H 89 c 0.394 0 0.75 0.23 0.911 0.589
            c 0.162 0.358 0.099 0.778 -0.162 1.073 L 57.353 40.191 c -1.891 2.141 -2.932 4.892 -2.932 7.748
            v 29.813 c 0 0.362 -0.196 0.697 -0.513 0.873 l -16.842 9.392 C 36.915 88.102 36.747 88.144 36.58 88.144 z'
                fill='black'
              />

              <path
                d='M 72.294 22.767 H 17.706 c -0.552 0 -1 -0.448 -1 -1 s 0.448 -1 1 -1 h 54.588 c 0.553 0 1 0.448 1 1
            S 72.847 22.767 72.294 22.767 z'
                fill='black'
              />
              <path
                d='M 63.37 32.869 H 26.63 c -0.552 0 -1 -0.448 -1 -1 s 0.448 -1 1 -1 h 36.74 c 0.553 0 1 0.448 1 1
            S 63.923 32.869 63.37 32.869 z'
                fill='black'
              />
            </g>
          </svg>
        </div>
      </div>
      {selectedLob && selectedSubLob && selectedProfile && (
        <div className='flex flex-col w-full mb-6'>
          <AddSection profileId={selectedProfile} />
          <Tabs
            value={selectedSectionId}
            onChange={handleTabChange}
            indicatorColor='primary'
            textColor='primary'
            variant='scrollable'
            scrollButtons='auto'
            className='appetite-tabs flex items-center w-full'
          >
            {tabSections.map((tab, index) => (
              <Tab
                key={tab.id}
                icon={<DescriptionIcon />}
                label={tab.name}
                value={tab.id}
                className={
                  selectedSectionId === tab.id
                    ? 'bg-green-500 tabs-list'
                    : 'tabs-list'
                }
              />
            ))}
          </Tabs>
          <Box py={3} className='appetite-content-block'>
            <h1 className='font-semibold pb-3 text-2xl text-orange-600'>
              Rules
            </h1>
            {errorMessage && (
              <Box pb={3}>
                <Typography color='error'>{errorMessage}</Typography>
              </Box>
            )}

            {successMessage && (
              <Box pb={3}>
                <Typography color='success'>{successMessage}</Typography>
              </Box>
            )}

            <TableContainer
              component={Paper}
              style={{
                borderRadius: '6px',
                overflow: 'hidden'
              }}
              className='mb-4 intake-table-data'
            >
              <Table
                sx={{ minWidth: 750 }}
                aria-labelledby='tableTitle'
                size={'medium'}
                className='rules-table'
              >
                <TableHead className='table-header white-text'>
                  <TableRow>
                    <TableCell key='sno' sortDirection={false}>
                      <Typography variant='subtitle2' fontWeight='bold'>
                        S.No
                      </Typography>
                    </TableCell>
                    <TableCell key='rule' sortDirection={false}>
                      <Typography variant='subtitle2' fontWeight='bold'>
                        Rule
                      </Typography>
                    </TableCell>
                    {/* <TableCell
                      key='effectiveDate'
                      sortDirection={false}
                      style={{ padding: '16px' }}
                    >
                      <Typography variant='subtitle2' fontWeight='bold'>
                        Effective Date
                      </Typography>
                    </TableCell> */}
                    <TableCell key='ruleType' sortDirection={false}>
                      <Typography variant='subtitle2' fontWeight='bold'>
                        Rule Type
                      </Typography>
                    </TableCell>
                    <TableCell
                      key='actions'
                      sortDirection={false}
                      style={{ textAlign: 'center' }}
                    >
                      <Typography variant='subtitle2' fontWeight='bold'>
                        Actions
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {softRules.length > 0 && (
                    <>
                      {softRules
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        )
                        .map((rule, index) => {
                          const rowNumber = getRowNumber(index);
                          return rule.isNew ? (
                            <TableRow
                              key={index}
                              className='hover:bg-gray-100'
                              style={{ height: '15px' }}
                            >
                              <TableCell
                                scope='row'
                                style={{ padding: '8px 16px' }}
                              >
                                {rowNumber}
                              </TableCell>
                              <TableCell style={{ padding: '8px 16px' }}>
                                <TextField
                                  value={rule.details}
                                  onChange={e => {
                                    const updatedRules = [...softRules];
                                    updatedRules[
                                      page * rowsPerPage + index
                                    ].details = e.target.value;
                                    setSoftRules(updatedRules);
                                  }}
                                  fullWidth
                                />
                              </TableCell>
                              {/* <TableCell style={{ padding: '8px 16px' }}>
                                <TextField
                                  type='date'
                                  value={rule.updated_at}
                                  onChange={e => {
                                    const updatedRules = [...softRules]
                                    updatedRules[
                                      page * rowsPerPage + index
                                    ].updated_at = e.target.value
                                    setSoftRules(updatedRules)
                                  }}
                                  fullWidth
                                />
                              </TableCell> */}
                              <TableCell style={{ padding: '8px 16px' }}>
                                <TextField
                                  select
                                  value={rule.rule_type}
                                  onChange={e => {
                                    const updatedRules = [...softRules];
                                    updatedRules[
                                      page * rowsPerPage + index
                                    ].rule_type = e.target.value;
                                    setSoftRules(updatedRules);
                                  }}
                                  fullWidth
                                >
                                  <MenuItem value='soft'>Soft</MenuItem>
                                  <MenuItem value='hard'>Hard</MenuItem>
                                </TextField>
                              </TableCell>
                              <TableCell
                                style={{
                                  padding: '8px 16px',
                                  textAlign: 'center'
                                }}
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                  }}
                                >
                                  <Tooltip title='Delete'>
                                    <IconButton
                                      onClick={() =>
                                        removeSoftRule(
                                          page * rowsPerPage + index
                                        )
                                      }
                                      color='secondary'
                                      aria-label='delete'
                                      size='large'
                                      disabled={isSelectedProfileActive}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Tooltip>
                                </div>
                              </TableCell>
                            </TableRow>
                          ) : (
                            <TableRow
                              key={index}
                              className='hover:bg-gray-100'
                              style={{ height: '15px' }}
                            >
                              <TableCell
                                scope='row'
                                style={{ padding: '8px 16px' }}
                              >
                                {rowNumber}
                              </TableCell>
                              <TableCell style={{ padding: '8px 16px' }}>
                                {rule.details}
                              </TableCell>
                              <TableCell style={{ padding: '8px 16px' }}>
                                {toCamelCase(rule.rule_type)}
                              </TableCell>
                              <TableCell
                                style={{
                                  padding: '8px 16px',
                                  textAlign: 'center'
                                }}
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                  }}
                                  className='rule-actions'
                                >
                                  <Tooltip title='Delete'>
                                    <IconButton
                                      onClick={() =>
                                        removeSoftRule(
                                          page * rowsPerPage + index
                                        )
                                      }
                                      color='secondary'
                                      aria-label='delete'
                                      size='large'
                                      disabled={isSelectedProfileActive}
                                    >
                                      <DeleteIcon sx={{ color: '#d21616' }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title='View Info'>
                                    <IconButton
                                      onClick={e => {
                                        handleOpenDialog(e, index, rule.id);
                                      }}
                                      color='primary'
                                      aria-label='info'
                                      size='large'
                                      style={{ cursor: 'pointer' }}
                                    >
                                      <InfoIcon sx={{ color: orange[500] }} />
                                    </IconButton>
                                  </Tooltip>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </>
                  )}
                  {softRules.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align='center'>
                        There are no rules found for the selection
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[10]}
                component='div'
                count={softRules.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                style={{ display: 'flex', justifyContent: 'center' }}
                classes={{
                  root: 'pagination-root',
                  toolbar: 'pagination-toolbar',
                  spacer: 'pagination-spacer',
                  selectLabel: 'pagination-selectLabel',
                  selectRoot: 'pagination-selectRoot',
                  select: 'pagination-select',
                  selectIcon: 'pagination-selectIcon',
                  input: 'pagination-input',
                  menuItem: 'pagination-menuItem',
                  actions: 'pagination-actions'
                }}
              />
            </TableContainer>
            <div className='flex justify-end'>
              <Button
                onClick={addSoftRule}
                color='primary'
                style={{
                  backgroundColor: isSelectedProfileActive ? '' : '#ea580c',
                  color: 'white',
                  marginTop: '20px'
                }}
                variant='contained'
                disabled={isSelectedProfileActive}
              >
                Add More
              </Button>
            </div>
          </Box>
          {!isSelectedProfileActive && (
            <div className='flex justify-center space-x-4 mt-6'>
              <Button
                variant='contained'
                color='primary'
                onClick={handlePublish}
              >
                Publish
              </Button>
              <Button variant='contained' color='primary' onClick={handleSave}>
                Save as draft
              </Button>
              <Button variant='contained' color='white' onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          )}
        </div>
      )}
      {/* <div className="empty-rules text-center py-6">
        No Rules
      </div> */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        sx={{ padding: '10px', marginBottom: '16px' }}
      >
        <div className='p-4'>
          <h1 className='py-2 text-2xl text-orange-600'>Properties</h1>
          {selectedRuleIndex !== null && (
            <>
              {ruleProperties.length > 0 ? (
                ruleProperties.map(property => (
                  <Box
                    key={property.id}
                    sx={{
                      display: 'flex',
                      marginBottom: '8px',
                      padding: '10px'
                    }}
                  >
                    <Typography sx={{ fontWeight: 'bold', marginRight: '8px' }}>
                      {property.key}:
                    </Typography>
                    <Typography>{property.value}</Typography>
                  </Box>
                ))
              ) : (
                <Typography>No Properties found</Typography>
              )}
            </>
          )}
        </div>
      </Popover>
    </div>
  );
};

export default AppetiteDocManagement;
